/**
 * useComprasData - Hook que centraliza a camada de DADOS do módulo Compras:
 * estados de referência (cotações, pedidos, empresas, fornecedores, produtos, etc.),
 * suas funções de fetch e o carregamento inicial.
 *
 * Extraído de Compras.jsx sem alteração de comportamento. O componente segue
 * dono de todo o estado de UI (modais, filtros, formulário) e dos handlers.
 */
import { useEffect, useState } from 'react';
import api from '../../lib/axios';

export default function useComprasData() {
  const [cotacoes, setCotacoes] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);
  const [tiposProduto, setTiposProduto] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [aprovadores, setAprovadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsVersion, setStatsVersion] = useState(0);

  const fetchCotacoes = async () => {
    try {
      const response = await api.get('/cotacoes', { params: { limit: 1000 } });
      setCotacoes(response.data.items || []);
      setStatsVersion(v => v + 1);
    } catch (error) {
      console.error('Erro ao carregar cotações:', error);
    }
  };

  const fetchPedidos = async () => {
    try {
      const response = await api.get('/pedidos-compra', { params: { limit: 1000 } });
      setPedidos(response.data.items || []);
      setStatsVersion(v => v + 1);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data.items || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };

  const fetchFornecedores = async () => {
    try {
      const response = await api.get('/fornecedores');
      setFornecedores(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const fetchTiposServico = async () => {
    try {
      const response = await api.get('/tipos-servico');
      setTiposServico(response.data.items || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de serviço:', error);
    }
  };

  const fetchProjetos = async () => {
    try {
      const response = await api.get('/projects');
      setProjetos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    }
  };

  const fetchProdutos = async () => {
    try {
      const response = await api.get('/produtos');
      // A API retorna lista direta ou objeto com produtos
      const data = response.data;
      const produtosData = Array.isArray(data) ? data : (data.produtos || []);
      setProdutos(produtosData);

      // Extrair categorias e marcas únicas
      const categoriasSet = new Set();
      const marcasSet = new Set();
      produtosData.forEach(p => {
        if (p.categoria) categoriasSet.add(p.categoria);
        if (p.marca) marcasSet.add(p.marca);
      });
      setCategorias([...categoriasSet].sort());
      setMarcas([...marcasSet].sort());
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const fetchTiposProduto = async () => {
    try {
      const response = await api.get('/tipos-produto');
      setTiposProduto(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de produto:', error);
    }
  };

  const fetchDashboardStats = async (range) => {
    const params = {};
    if (range?.inicio) params.data_inicio = range.inicio.toISOString();
    if (range?.fim) params.data_fim = range.fim.toISOString();
    const response = await api.get('/compras/dashboard', { params });
    return response.data;
  };

  const fetchEvolucaoMensal = async (range, moeda) => {
    const params = {};
    if (range?.inicio) params.data_inicio = range.inicio.toISOString();
    if (range?.fim) params.data_fim = range.fim.toISOString();
    if (moeda) params.moeda = moeda;
    const response = await api.get('/compras/dashboard/evolucao-mensal', { params });
    return response.data;
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCotacoes(),
        fetchPedidos(),
        fetchEmpresas(),
        fetchFornecedores(),
        fetchTiposServico(),
        fetchProjetos(),
        fetchProdutos(),
        fetchTiposProduto(),
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
    // Carregar aprovadores disponíveis
    api.get('/cotacoes/aprovadores-disponiveis')
      .then(r => setAprovadores(r.data || []))
      .catch(() => setAprovadores([]));
  }, []);

  return {
    // dados
    cotacoes, setCotacoes,
    pedidos, setPedidos,
    empresas, setEmpresas,
    fornecedores, setFornecedores,
    tiposServico, setTiposServico,
    tiposProduto, setTiposProduto,
    projetos, setProjetos,
    produtos, setProdutos,
    categorias, marcas,
    aprovadores, setAprovadores,
    loading, setLoading,
    statsVersion, setStatsVersion,
    // fetchers
    fetchCotacoes,
    fetchPedidos,
    fetchEmpresas,
    fetchFornecedores,
    fetchTiposServico,
    fetchProjetos,
    fetchProdutos,
    fetchTiposProduto,
    fetchDashboardStats,
    fetchEvolucaoMensal,
    loadInitialData,
  };
}
